/**
 * GuidedWalkthrough — a polished, multi-page onboarding tour.
 *
 * Unlike the old single-page joyride, this engine:
 *  - lives in FacilityLayout so it SURVIVES route changes,
 *  - auto-navigates the user across the key pages (Live Site View → Workers →
 *    Ergonomics → Reports → Settings),
 *  - spotlights a real on-screen element per step (with a graceful centered
 *    fallback when a target isn't found, e.g. on mobile where the nav is in a
 *    drawer),
 *  - renders an on-brand card (progress, Back/Next/Skip) instead of generic
 *    library bubbles.
 *
 * Persistence:
 *  - Production: runs once per browser (localStorage `sentry.onboarding.v2.done`).
 *  - Demo stack (VITE_IS_DEMO): runs once per session (sessionStorage) and stays
 *    re-launchable via a floating button so prospects can replay it.
 *
 * To replay in prod: localStorage.removeItem('sentry.onboarding.v2.done').
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Fab,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  ViewInAr as ViewInArIcon,
  Speed as SpeedIcon,
  NotificationsActive as AlertIcon,
  People as PeopleIcon,
  Accessibility as ErgoIcon,
  Assessment as ReportIcon,
  Settings as SettingsIcon,
  Shield as ShieldIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { isDemoMode } from '../../utils/demoMode';

const DONE_KEY = 'sentry.onboarding.v2.done';
const DEMO_SEEN_KEY = 'sentry.onboarding.v2.demo.seen';

type Placement = 'center' | 'top' | 'bottom' | 'left' | 'right';

interface WalkStep {
  id: string;
  /** Facility route segment to navigate to before showing this step. */
  segment?: string;
  /** CSS selector to spotlight. Omit for a centered card. */
  target?: string;
  title: string;
  body: string;
  icon: React.ReactNode;
  placement?: Placement;
  /** Roles allowed to see this step (omit = everyone). */
  roles?: string[];
}

function buildSteps(demo: boolean): WalkStep[] {
  return [
    {
      id: 'welcome',
      title: demo ? 'Welcome to the Sentry Solutions demo' : 'Welcome to Sentry Solutions',
      body: demo
        ? "This is a live, fully-simulated demo facility; explore freely, nothing here affects a real site. Take this 2-minute tour to see how Sentry Solutions keeps workers safe in real time."
        : "Let's take a quick 2-minute tour of your worker-safety command center (the live floor, safety alerts, ergonomics, predictive risk, and reporting) and show you where everything lives.",
      icon: <ShieldIcon fontSize="large" />,
      placement: 'center',
    },
    {
      id: 'live-floor',
      segment: 'dashboard',
      target: '[data-tour="floor-plan"]',
      title: 'Your live 3D floor',
      body: 'A real-time digital twin of the facility. Every avatar is a worker positioned by UWB sensors. Body posture is captured at 100 Hz on the wearable IMUs, buffered on the sensor, and streamed to the cloud at 2 Hz; no cameras, no manual check-ins.',
      icon: <ViewInArIcon fontSize="large" />,
      placement: 'top',
    },
    {
      id: 'kpis',
      segment: 'dashboard',
      target: '[data-tour="kpi-row"]',
      title: 'Real-time KPIs',
      body: 'Active workers, open alerts, ergonomic risk, and overall safety score: the whole floor at a glance, updating live.',
      icon: <SpeedIcon fontSize="large" />,
      placement: 'top',
    },
    {
      id: 'alerts',
      segment: 'dashboard',
      target: '[data-tour="alert-panel"]',
      title: 'Instant safety alerts',
      body: 'Slips, falls, prolonged awkward postures, and restricted-zone entries surface the moment they happen. Critical alerts escalate automatically to the right people.',
      icon: <AlertIcon fontSize="large" />,
      placement: 'left',
    },
    {
      id: 'workers',
      segment: 'workers',
      target: '[data-tour="nav-workers"]',
      title: 'Workers',
      body: 'Your full roster with per-worker drill-down: live status, movement history, ergonomic and fatigue trends, and assigned devices.',
      icon: <PeopleIcon fontSize="large" />,
      placement: 'right',
    },
    {
      id: 'ergonomics',
      segment: 'ergonomics',
      target: '[data-tour="nav-ergonomics"]',
      title: 'Ergonomics & posture risk',
      body: 'Five Motion Class ergonomic scoring and posture-risk trends per worker and zone, powered by dual-sensor fusion that distinguishes a safe squat from a risky back-bend.',
      icon: <ErgoIcon fontSize="large" />,
      placement: 'right',
    },
    {
      id: 'reports',
      segment: 'reports',
      target: '[data-tour="nav-reports"]',
      title: 'Reports & compliance',
      body: 'OSHA-ready logs, safety summaries, and CSV/PDF export. turn continuous sensor data into the records auditors and leadership ask for.',
      icon: <ReportIcon fontSize="large" />,
      placement: 'right',
    },
    {
      id: 'settings',
      segment: 'settings',
      target: '[data-tour="nav-settings"]',
      title: 'Configure your facility',
      body: 'Set up zones, anchors, devices, alert rules, and your team. Everything that tailors Sentry Solutions to your floor lives here.',
      icon: <SettingsIcon fontSize="large" />,
      placement: 'right',
      roles: ['ADMIN', 'SUPERVISOR'],
    },
    {
      id: 'finish',
      segment: 'dashboard',
      title: "You're all set",
      body: demo
        ? "That's the tour. Click around freely, and when you're ready to protect your own floor, use Contact Sales any time."
        : 'Explore at your own pace. You can replay this tour anytime, and your Sentry Solutions rep is one click away if you need a hand.',
      icon: <CheckCircleIcon fontSize="large" />,
      placement: 'center',
    },
  ];
}

const CARD_W = 380;
const CARD_H = 280;
const MARGIN = 16;

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

function computeCardPos(
  rect: DOMRect | null,
  placement: Placement,
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect || placement === 'center') {
    return { top: Math.max(MARGIN, vh / 2 - CARD_H / 2), left: vw / 2 - CARD_W / 2 };
  }
  let top: number;
  let left: number;
  switch (placement) {
    case 'top':
      top = rect.top - CARD_H - MARGIN;
      left = rect.left + rect.width / 2 - CARD_W / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - CARD_H / 2;
      left = rect.left - CARD_W - MARGIN;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - CARD_H / 2;
      left = rect.right + MARGIN;
      break;
    case 'bottom':
    default:
      top = rect.bottom + MARGIN;
      left = rect.left + rect.width / 2 - CARD_W / 2;
      break;
  }
  // If the chosen side overflows, fall back to the opposite / center-ish.
  if (placement === 'top' && top < MARGIN) top = rect.bottom + MARGIN;
  if (placement === 'bottom' && top + CARD_H > vh - MARGIN) top = rect.top - CARD_H - MARGIN;
  if (placement === 'left' && left < MARGIN) left = rect.right + MARGIN;
  if (placement === 'right' && left + CARD_W > vw - MARGIN) left = rect.left - CARD_W - MARGIN;
  return {
    top: clamp(top, MARGIN, vh - CARD_H - MARGIN),
    left: clamp(left, MARGIN, vw - CARD_W - MARGIN),
  };
}

function waitForElement(selector: string, timeoutMs = 3000): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const start = performance.now();
    const tick = () => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el && el.getBoundingClientRect().width > 0) {
        resolve(el);
        return;
      }
      if (performance.now() - start > timeoutMs) {
        resolve(null);
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}

export default function GuidedWalkthrough() {
  const navigate = useNavigate();
  const location = useLocation();
  const { facilityId } = useParams<{ facilityId: string }>();
  const currentUser = useSelector(selectCurrentUser);
  const demo = isDemoMode();

  const role = (currentUser?.role || '').toUpperCase();
  const steps = useRef<WalkStep[]>([]);
  if (steps.current.length === 0) {
    steps.current = buildSteps(demo)
      .filter((s) => !s.roles || s.roles.includes(role));
  }
  const allSteps = steps.current;

  const [running, setRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const cancelRef = useRef(false);

  const basePath = `/facilities/${facilityId}`;
  const step = allSteps[stepIndex];

  // Auto-start once (first visit / first session).
  useEffect(() => {
    if (!facilityId) return;
    let seen: string | null = null;
    try {
      seen = demo
        ? sessionStorage.getItem(DEMO_SEEN_KEY)
        : localStorage.getItem(DONE_KEY);
    } catch {
      seen = null;
    }
    if (seen) return;
    const t = setTimeout(() => {
      setStepIndex(0);
      setRunning(true);
    }, 1300);
    return () => clearTimeout(t);
  }, [facilityId, demo]);

  // Drive navigation + target spotlight for the active step.
  useEffect(() => {
    if (!running || !step) return;
    cancelRef.current = false;
    setRect(null);

    (async () => {
      if (step.segment) {
        const dest = `${basePath}/${step.segment}`;
        if (!location.pathname.startsWith(dest)) {
          navigate(dest);
        }
      }
      if (!step.target) return; // centered card
      const el = await waitForElement(step.target);
      if (cancelRef.current) return;
      if (el) {
        try {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch {
          /* older browsers */
        }
        await new Promise((r) => setTimeout(r, 280));
        if (cancelRef.current) return;
        setRect(el.getBoundingClientRect());
      }
    })();

    return () => {
      cancelRef.current = true;
    };
  }, [running, stepIndex, basePath]);

  // Keep the spotlight aligned on scroll/resize.
  useEffect(() => {
    if (!running || !step?.target) return;
    const reposition = () => {
      const el = document.querySelector(step.target!) as HTMLElement | null;
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [running, step, stepIndex]);

  const finish = useCallback(() => {
    setRunning(false);
    setRect(null);
    try {
      if (demo) sessionStorage.setItem(DEMO_SEEN_KEY, '1');
      else localStorage.setItem(DONE_KEY, '1');
    } catch {
      /* storage unavailable */
    }
  }, [demo]);

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (i >= allSteps.length - 1) {
        finish();
        return i;
      }
      return i + 1;
    });
  }, [allSteps.length, finish]);

  const back = useCallback(() => setStepIndex((i) => Math.max(0, i - 1)), []);

  const startTour = useCallback(() => {
    setStepIndex(0);
    setRunning(true);
  }, []);

  // Keyboard: Esc skips, ArrowRight next, ArrowLeft back.
  useEffect(() => {
    if (!running) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [running, finish, next, back]);

  if (!facilityId) return null;

  const isLast = stepIndex >= allSteps.length - 1;
  const pos = running && step ? computeCardPos(rect, step.placement ?? 'center') : { top: 0, left: 0 };

  return (
    <>
      {running && step && (
        <Box
          sx={{ position: 'fixed', inset: 0, zIndex: 13000 }}
          role="dialog"
          aria-modal="true"
          aria-label="Product tour"
        >
          {/* Backdrop + spotlight */}
          {rect ? (
            <Box
              sx={{
                position: 'fixed',
                top: rect.top - 6,
                left: rect.left - 6,
                width: rect.width + 12,
                height: rect.height + 12,
                borderRadius: 2,
                boxShadow: '0 0 0 9999px rgba(15,23,42,0.72)',
                border: '2px solid',
                borderColor: 'primary.light',
                pointerEvents: 'none',
                transition: 'all 0.3s ease',
              }}
            />
          ) : (
            <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(15,23,42,0.72)' }} />
          )}

          {/* Card */}
          <Fade in timeout={250} key={step.id}>
            <Paper
              elevation={16}
              sx={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                width: CARD_W,
                maxWidth: 'calc(100vw - 32px)',
                p: 3,
                borderRadius: 3,
                borderTop: '4px solid',
                borderColor: 'primary.main',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                <Box sx={{ color: 'primary.main', mt: 0.25 }}>{step.icon}</Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
                    Step {stepIndex + 1} of {allSteps.length}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                    {step.title}
                  </Typography>
                </Box>
                <Tooltip title="Skip tour">
                  <IconButton size="small" onClick={finish} aria-label="Skip tour" sx={{ mt: -0.5, mr: -0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <LinearProgress
                variant="determinate"
                value={((stepIndex + 1) / allSteps.length) * 100}
                sx={{ mb: 2, borderRadius: 1, height: 6 }}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, minHeight: 72 }}>
                {step.body}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button size="small" color="inherit" onClick={finish} sx={{ color: 'text.secondary' }}>
                  Skip
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {stepIndex > 0 && (
                  <Button size="small" variant="outlined" onClick={back}>
                    Back
                  </Button>
                )}
                <Button size="small" variant="contained" onClick={next} data-testid="tour-next">
                  {isLast ? 'Finish' : 'Next'}
                </Button>
              </Box>
            </Paper>
          </Fade>
        </Box>
      )}

      {/* Re-launch button (demo only) */}
      {demo && !running && (
        <Tooltip title="Take the guided tour" placement="left">
          <Fab
            color="primary"
            size="medium"
            onClick={startTour}
            aria-label="Take the guided tour"
            data-testid="tour-launcher"
            sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}
          >
            <PlayArrowIcon />
          </Fab>
        </Tooltip>
      )}
    </>
  );
}
